"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BoundsGenerator = void 0;
/*
 * Copyright (C) 2017-2020 HERE Europe B.V.
 * Licensed under Apache 2.0, see full license in LICENSE
 * SPDX-License-Identifier: Apache-2.0
 */
const harp_geoutils_1 = require("@here/harp-geoutils");
const harp_utils_1 = require("@here/harp-utils");
const three_1 = require("three");
const Utils_1 = require("./Utils");
const logger = harp_utils_1.LoggerManager.instance.create("BoundsGenerator");
/**
 * Generates Bounds for a camera view and a projection
 */
class BoundsGenerator {
    constructor(m_camera, m_projection, m_tileWrappingEnabled = false) {
        this.m_camera = m_camera;
        this.m_projection = m_projection;
        this.m_tileWrappingEnabled = m_tileWrappingEnabled;
        this.m_groundPlaneNormal = new three_1.Vector3(0, 0, 1);
        this.m_groundPlane = new three_1.Plane(this.m_groundPlaneNormal.clone());
    }
    set projection(projection) {
        this.m_projection = projection;
    }
    /**
     * Generates an Array of GeoCoordinates covering the visible map.
     * The coordinates are sorted to ccw winding, so a polygon could be drawn with them.
     */
    generate() {
        //TODO: support spherical projection
        //!!!!!!!ALTITUDE IS NOT TAKEN INTO ACCOUNT!!!!!!!!!
        const geoPolygon = [];
        //CASE A: FLAT PROJECTION
        if (this.m_projection.type === harp_geoutils_1.ProjectionType.Planar) {
            // 1.) Raycast into all four corners of the canvas
            //     => if an intersection is found, add it to the polygon
            this.addCanvasCornerIntersection(geoPolygon);
            // => All 4 corners found an intersection, therefore the screen is covered with the map
            // and the polygon complete
            if (geoPolygon.length === 4) {
                return geoPolygon;
            }
            //2.) Raycast into the two corners of the horizon cutting the canvas sides
            //    => if an intersection is found, add it to the polygon
            this.addHorizonIntersection(geoPolygon);
            //Setup the frustum for further checks
            const frustum = new three_1.Frustum().setFromProjectionMatrix(new three_1.Matrix4().multiplyMatrices(this.m_camera.projectionMatrix, this.m_camera.matrixWorldInverse));
            // Setup the world corners for further checks.
            // Cast to TileCorners as it cannot be undefined here, due to the forced
            // PlanarProjection above
            const worldCorners = this.getWorldConers(this.m_projection);
            if (!this.m_tileWrappingEnabled) {
                // 3.) If no wrapping, check if any corners of the world plane are inside the view
                //     => if true, add it to the polygon
                [worldCorners.ne, worldCorners.nw, worldCorners.se, worldCorners.sw].forEach(corner => {
                    this.addPointInFrustum(corner, frustum, geoPolygon);
                });
            }
            //4.) Check for any edges of the world plane intersecting with the frustum?
            //    => if true, add to polygon
            if (!this.m_tileWrappingEnabled) {
                // if no tile wrapping:
                //       check with limited lines around the world edges
                [
                    new three_1.Line3(worldCorners.sw, worldCorners.se),
                    new three_1.Line3(worldCorners.ne, worldCorners.nw),
                    new three_1.Line3(worldCorners.se, worldCorners.ne),
                    new three_1.Line3(worldCorners.nw, worldCorners.sw) //  west edge
                ].forEach(edge => {
                    this.addFrustumIntersection(edge, frustum, geoPolygon);
                });
            }
            else {
                // if tile wrapping:
                //       check for intersections with rays along the south and north edges
                const directionEast = new three_1.Vector3() //west -> east
                    .subVectors(worldCorners.sw, worldCorners.se)
                    .normalize();
                const directionWest = new three_1.Vector3() //east -> west
                    .subVectors(worldCorners.se, worldCorners.sw)
                    .normalize();
                [
                    new three_1.Ray(worldCorners.se, directionEast),
                    new three_1.Ray(worldCorners.se, directionWest),
                    new three_1.Ray(worldCorners.ne, directionEast),
                    new three_1.Ray(worldCorners.ne, directionWest) //  north west ray
                ].forEach(ray => {
                    this.addFrustumIntersection(ray, frustum, geoPolygon);
                });
            }
        }
        else {
            logger.error("This ProjectionType", this.m_projection, " is not yet supported!");
        }
        // 5.) Sort the coordinates in the polygon to be ccw, assuming the coordinates are creating
        //     a convex shape.
        this.sortCCW(geoPolygon);
        return geoPolygon;
    }
    getWorldConers(projection) {
        if (projection.type !== harp_geoutils_1.ProjectionType.Planar) {
            return;
        }
        const worldBox = projection.worldExtent(0, 0);
        return {
            sw: worldBox.min,
            se: new three_1.Vector3(worldBox.max.x, worldBox.min.y, 0),
            nw: new three_1.Vector3(worldBox.min.x, worldBox.max.y, 0),
            ne: worldBox.max
        };
    }
    addNDCRayIntersection(ndcPoints, geoPolygon) {
        ndcPoints.forEach(corner => {
            const intersection = Utils_1.MapViewUtils.rayCastWorldCoordinates({ camera: this.m_camera, projection: this.m_projection }, corner[0], corner[1]);
            if (intersection) {
                this.validateAndAddToGeoPolygon(intersection, geoPolygon);
            }
        });
    }
    addHorizonIntersection(geoPolygon) {
        const verticalHorizonPosition = this.getVerticalHorizonPositionInNDC();
        if (!verticalHorizonPosition) {
            return;
        }
        this.addNDCRayIntersection([
            [-1, verticalHorizonPosition],
            [1, verticalHorizonPosition] //horizon right
        ], geoPolygon);
    }
    addCanvasCornerIntersection(geoPolygon) {
        this.addNDCRayIntersection([
            [-1, -1],
            [-1, 1],
            [1, 1],
            [1, -1] //lower right
        ], geoPolygon);
    }
    validateAndAddToGeoPolygon(point, geoPolygon) {
        if (this.isInVisibleMap(point)) {
            geoPolygon.push(this.m_projection.unprojectPoint(point));
        }
    }
    isInVisibleMap(point) {
        if (this.m_projection.type === harp_geoutils_1.ProjectionType.Planar) {
            if (point.y < 0 || point.y > harp_geoutils_1.EarthConstants.EQUATORIAL_CIRCUMFERENCE) {
                return false;
            }
            if (!this.m_tileWrappingEnabled &&
                (point.x < 0 || point.x > harp_geoutils_1.EarthConstants.EQUATORIAL_CIRCUMFERENCE)) {
                return false;
            }
        }
        return true;
    }
    sortCCW(coordinates) {
        if (coordinates.length < 2) {
            return;
        }
        const polySum = coordinates.reduce((prev, curr) => {
            return new harp_geoutils_1.GeoCoordinates(prev.latitude + curr.latitude, prev.longitude + curr.longitude);
        });
        //create an average center point to rotate around
        const polyCenter = new harp_geoutils_1.GeoCoordinates(polySum.latitude / coordinates.length, polySum.longitude / coordinates.length);
        //sorts by angle from x-axis
        coordinates.sort((a, b) => {
            const veca = new three_1.Vector2(a.latitude - polyCenter.latitude, a.longitude - polyCenter.longitude).normalize();
            const vecb = new three_1.Vector2(b.latitude - polyCenter.latitude, b.longitude - polyCenter.longitude).normalize();
            return vecb.angle() - veca.angle();
        });
    }
    addPointInFrustum(point, frustum, geoPolygon) {
        if (frustum.containsPoint(point)) {
            const geoPoint = this.m_projection.unprojectPoint(point);
            geoPoint.altitude = 0;
            geoPolygon.push(geoPoint);
        }
    }
    addFrustumIntersection(edge, frustum, geoPolygon) {
        frustum.planes.forEach(plane => {
            let intersection = null;
            const target = new three_1.Vector3();
            if (edge instanceof three_1.Ray && edge.intersectsPlane(plane)) {
                intersection = edge.intersectPlane(plane, target);
            }
            else if (edge instanceof three_1.Line3 && plane.intersectsLine(edge)) {
                intersection = plane.intersectLine(edge, target);
            }
            if (intersection) {
                //uses this check to fix inaccuracies
                if (this.closeToFrustum(intersection)) {
                    const geoIntersection = this.m_projection.unprojectPoint(intersection);
                    //correct altitude caused by inaccuracies, due to large numbers to 0
                    geoIntersection.altitude = 0;
                    geoPolygon.push(geoIntersection);
                }
            }
        });
    }
    closeToFrustum(point) {
        const e = 0.0000000001;
        const ndcPoint = new three_1.Vector3().copy(point).project(this.m_camera);
        if (Math.abs(ndcPoint.x) - e < 1 &&
            Math.abs(ndcPoint.y) - e < 1 &&
            Math.abs(ndcPoint.z) - e < 1) {
            return true;
        }
        return false;
    }
    getVerticalHorizonPositionInNDC() {
        if (this.m_projection.type !== harp_geoutils_1.ProjectionType.Planar) {
            return undefined;
        }
        const bottomMidFarPoint = new three_1.Vector3(-1, -1, 1)
            .unproject(this.m_camera)
            .add(new three_1.Vector3(1, -1, 1).unproject(this.m_camera))
            .multiplyScalar(0.5);
        const topMidFarPoint = new three_1.Vector3(-1, 1, 1)
            .unproject(this.m_camera)
            .add(new three_1.Vector3(1, 1, 1).unproject(this.m_camera))
            .multiplyScalar(0.5);
        const farPlaneVerticalCenterLine = new three_1.Line3(bottomMidFarPoint, topMidFarPoint);
        const verticalHorizonPosition = new three_1.Vector3();
        if (!this.m_groundPlane.intersectLine(farPlaneVerticalCenterLine, verticalHorizonPosition)) {
            return undefined;
        }
        return verticalHorizonPosition.project(this.m_camera).y;
    }
}
exports.BoundsGenerator = BoundsGenerator;
//# sourceMappingURL=BoundsGenerator.js.map